import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  Title,
  Text,
  Tabs,
  Group,
  ThemeIcon,
  Paper,
  Alert,
  useMantineColorScheme
} from '@mantine/core';
import {
  IconRobot,
  IconChartBar,
  IconFileText,
  IconAlignLeft,
  IconSettings,
  IconInfoCircle
} from '@tabler/icons-react';
import OverviewTab from './components/OverviewTab';
import DocumentsTab from './components/DocumentsTab';
import TextEntriesTab from './components/TextEntriesTab';
import SystemPromptTab from './components/SystemPromptTab';

const AIKnowledge = () => {
  const { t } = useTranslation();
  const { colorScheme } = useMantineColorScheme();
  const [activeTab, setActiveTab] = useState<string | null>('overview');
  const [refreshKey, setRefreshKey] = useState(0);

  const triggerRefresh = () => setRefreshKey(k => k + 1);

  return (
    <Stack gap="lg">
      {/* Header */}
      <Paper
        p="xl"
        radius="md"
        style={{
          background: colorScheme === 'dark'
            ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)'
            : 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)',
          border: `1px solid ${colorScheme === 'dark' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)'}`
        }}
      >
        <Group gap="md" align="flex-start">
          <ThemeIcon
            size={48}
            radius="md"
            variant="gradient"
            gradient={{ from: 'blue', to: 'violet', deg: 135 }}
          >
            <IconRobot size={28} />
          </ThemeIcon>
          <div style={{ flex: 1 }}>
            <Title order={2} fw={700}>
              {t('aiKnowledge.title')}
            </Title>
            <Text size="sm" c="dimmed" mt={4} maw={700}>
              {t('aiKnowledge.description')}
            </Text>
          </div>
        </Group>
      </Paper>

      {/* How it works info */}
      <Alert
        variant="light"
        color="blue"
        icon={<IconInfoCircle size={20} />}
        title={t('aiKnowledge.howItWorks.title')}
        styles={{
          title: { fontWeight: 600 }
        }}
      >
        <Text size="sm" style={{ lineHeight: 1.6 }}>
          {t('aiKnowledge.howItWorks.description')}
        </Text>
      </Alert>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={setActiveTab} variant="outline" radius="md">
        <Tabs.List>
          <Tabs.Tab
            value="overview"
            leftSection={<IconChartBar size={16} />}
          >
            {t('aiKnowledge.tabs.overview')}
          </Tabs.Tab>
          <Tabs.Tab
            value="documents"
            leftSection={<IconFileText size={16} />}
          >
            {t('aiKnowledge.tabs.documents')}
          </Tabs.Tab>
          <Tabs.Tab
            value="texts"
            leftSection={<IconAlignLeft size={16} />}
          >
            {t('aiKnowledge.tabs.textEntries')}
          </Tabs.Tab>
          <Tabs.Tab
            value="prompt"
            leftSection={<IconSettings size={16} />}
          >
            {t('aiKnowledge.tabs.systemPrompt')}
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="overview" pt="md">
          <OverviewTab refreshKey={refreshKey} />
        </Tabs.Panel>

        <Tabs.Panel value="documents" pt="md">
          <DocumentsTab onDataChange={triggerRefresh} />
        </Tabs.Panel>

        <Tabs.Panel value="texts" pt="md">
          <TextEntriesTab onDataChange={triggerRefresh} />
        </Tabs.Panel>

        <Tabs.Panel value="prompt" pt="md">
          <SystemPromptTab />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
};

export default AIKnowledge;
